import { fetchCountry, fetchSubject } from "../services/contactService";
import { defineStore } from "pinia";
import type { Country } from "~/types/Country";
import type { Language } from "~/types/Language";

export const useLocationStore = defineStore({
  id: "location-store",
  state: () => {
    return {
      _countries: [] as Country[],
      _languages: [] as Language[],
      _countryStates: [] as any[],
      _timezones: [] as any[],
      _cities: [] as any[],
      _loadingCountries: true,
      _countriesError: false,
      _loadingLanguages: true,
      _loadingCountryStates: false,
      _loadingTimezones: false,
      _searchingCity: false,
      _searchingCityTimeout: null as unknown as NodeJS.Timeout,
      $http: useNuxtApp().$axios,
    };
  },
  actions: {
    async getAllCountries() {
      this._loadingCountries = true;
      this._countriesError = false;
      try {
        const { data } = await this.$http.get("/api/v1/countries?per_page=all");
        // The API returns the 250 rows in database order (Åland, American
        // Samoa, Anguilla, Antarctica, Bermuda, Bouvet Island...), and the
        // ComboBox only sorts once you type — so scrolling for your country was
        // useless. Sort by the localised name the ComboBox actually displays.
        this._countries = (data.data as Country[]).slice().sort((a: any, b: any) =>
          String(a?.localized_name ?? a?.name ?? "").localeCompare(
            String(b?.localized_name ?? b?.name ?? ""),
          ),
        );
      } catch (error) {
        // Without this the loading flag stayed true forever and every country
        // ComboBox rendered permanently disabled with no error shown. The
        // ComboBox itself still renders an empty, silent dropdown though —
        // _countriesError lets the register form show a real message + retry
        // instead of a required field with no visible options and no reason
        // why (this blocks step 2 entirely, since country_id is required).
        this._countries = [];
        this._countriesError = true;
      } finally {
        this._loadingCountries = false;
      }
    },
    async getAllLanguages() {
      const { data } = await this.$http.get("/api/v1/languages?filter[status]=1&filter[status_public]=1&per_page=10&page=1&sort=id&order=asc");
      this._loadingLanguages = false;
      this._languages = data.data;
    },
    async getAllTimezones() {
      const { data } = await this.$http.get("/api/v1/timezone?per_page=all");
      this._loadingTimezones = false;
      this._timezones = data.data;
    },
    loadCountryStates(countryId: string) {
      if (!countryId) return new Promise((resolve) => resolve(true));
      this._countryStates = [];
      this._loadingCountryStates = true;
      return this.$http
        .get(`/api/v1/countries/${countryId}/states?per_page=all`)
        .then((response) => {
          this._loadingCountryStates = false;
          this._countryStates = response.data.data;
        })
        .catch((error) => {
          this._loadingCountryStates = true;
          this._countryStates = [];
        });
    },
    searchCity(countryId: number, stateId: number, city: string) {
      // eslint-disable-next-line @typescript-eslint/ban-types
      const debounce = (func: Function, delay: number) => {
        return (...args: any[]) => {
          clearTimeout(this._searchingCityTimeout);
          this._searchingCityTimeout = setTimeout(() => func(...args), delay);
        };
      };

      const debouncedSearch = debounce(() => {
        this._searchingCity = true;
        return this.$http
          .get(`/api/v1/countries/${countryId}/states/${stateId}/search-city/${city}`, {
            metadata: { showLoading: true },
          })
          .then((response) => {
            this._searchingCity = false;
            this._cities = response.data.data;
          })
          .catch((error) => {
            this._searchingCity = true;
            this._cities = [];
          });
      }, 300);

      return new Promise((resolve, reject) => {
        debouncedSearch();
        resolve(true);
      });
    },
  },
  getters: {
    countries(): Country[] {
      return this._countries;
    },
    languages(): any[] {
      return this._languages;
    },
    loadingCountries(): boolean {
      return this._loadingCountries;
    },
    countriesError(): boolean {
      return this._countriesError;
    },
    loadingLanguages(): boolean {
      return this._loadingLanguages;
    },
    countryStates(): any[] {
      return this._countryStates;
    },
    loadingCountryStates(): boolean {
      return this._loadingCountryStates;
    },
    cities(): any[] {
      return this._cities;
    },
    searchingCity(): boolean {
      return this._searchingCity;
    },
    loadingTimezones(): boolean {
      return this._loadingTimezones;
    },
    timezones(): any[] {
      return this._timezones;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInstitutionalStore, import.meta.hot));
}
